/**
 * Profile documents include a `plates` array; each plate has its own `_id` and `status`.
 * User-type profiles use `userId`, business profiles use `user`.
 */

export function getProfileUser(profile) {
  if (!profile) return {}
  return profile.userId || profile.user || {}
}

export function getPlateNumber(plateEntry, profile) {
  if (plateEntry) {
    return (
      plateEntry.numberPlate ||
      plateEntry.plateNumber ||
      plateEntry.licensePlateText ||
      "N/A"
    )
  }
  return (
    profile?.licensePlateText ||
    profile?.numberPlate ||
    profile?.plateNumber ||
    "N/A"
  )
}

export function getPlateCaption(plateEntry) {
  if (!plateEntry) return ""
  return plateEntry.caption || plateEntry.plateCaption || ""
}

/**
 * Image shown as "plate photo" in tables and detail pages: plate-specific URL first,
 * then profile cover (`coverPictureUrl` user / `coverImage` business).
 */
export function getPlateDisplayImageUrl(plateEntry, profile) {
  const userType = String(
    profile?.profileType || profile?.userType || getProfileUser(profile)?.userType || ""
  ).toLowerCase()

  // For user profiles, prefer the uploaded cover image in tables.
  if (userType === "user") {
    const userCover =
      profile?.coverPictureUrl ||
      profile?.coverImage ||
      ""
    if (userCover) return userCover
  }

  const fromPlate =
    plateEntry?.licensePlateImageUrl || plateEntry?.plateImage || ""
  const fromProfile =
    profile?.licensePlateImageUrl ||
    profile?.plateImage ||
    profile?.coverPictureUrl ||
    profile?.coverImage ||
    ""
  return fromPlate || fromProfile || ""
}

export function getProfileDisplayName(profile) {
  if (!profile) return "N/A"
  const u = getProfileUser(profile)
  return (
    profile.businessName ||
    profile.profilename ||
    profile.username ||
    u.username ||
    `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
    u.email ||
    "N/A"
  )
}

export function getProfileEmail(profile) {
  if (!profile) return "N/A"
  const u = getProfileUser(profile)
  return profile.businessEmail || u.email || "N/A"
}

export function getProfileUserImage(profile) {
  if (!profile) return process.env.PUBLIC_URL + "/images/user.svg"
  const u = getProfileUser(profile)
  return (
    profile.profilePictureUrl ||
    profile.profileImage ||
    u.profilePictureUrl ||
    u.profileImage ||
    process.env.PUBLIC_URL + "/images/user.svg"
  )
}

/**
 * Plate and profile may each have `state` / `country` (profiles often use state, not city).
 */
export function getPlateLocationState(plateEntry, profile) {
  if (!profile && !plateEntry) return ""
  return (
    plateEntry?.state ||
    profile?.state ||
    profile?.city ||
    ""
  )
}

export function getPlateLocationCountry(plateEntry, profile) {
  if (!profile && !plateEntry) return ""
  return plateEntry?.country || profile?.country || ""
}

export function mapPlateStatusLabel(status) {
  if (!status) return "—"
  const statusMap = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    reported: "Reported",
    open: "Open",
    resolved: "Resolved",
    reviewed: "Reviewed",
  }
  const key = String(status).toLowerCase()
  return (
    statusMap[key] ||
    String(status).charAt(0).toUpperCase() + String(status).slice(1)
  )
}

/**
 * Yields one row per plate whose `status` matches `targetStatus`.
 * @param {Array} profiles - API `data` array
 * @param {string} targetStatus - e.g. "pending", "approved", "reported"
 * @returns {Array<{ profile: object, plateEntry: object, plateId: string }>}
 */
export function flattenProfilesByPlateStatus(profiles, targetStatus) {
  const out = []
  const raw = Array.isArray(profiles) ? profiles : []
  const want = String(targetStatus || "").toLowerCase()

  for (const profile of raw) {
    const platesArr = Array.isArray(profile.plates) ? profile.plates : []
    for (const p of platesArr) {
      if (!p || !p._id) continue
      if (String(p.status || "").toLowerCase() !== want) continue
      out.push({ profile, plateEntry: p, plateId: p._id })
    }
  }

  return out
}

/** Plates on /reported-plates: show every plate whose status is not these (e.g. reported, rejected). */
export const REPORTED_PLATES_EXCLUDED_STATUSES = [
  "pending",
  "approved",
  "resolved",
]

/**
 * One row per plate whose status is not in `excludedStatuses` (case-insensitive).
 */
export function flattenProfilesExcludingPlateStatuses(profiles, excludedStatuses) {
  const excluded = new Set(
    (excludedStatuses || []).map((s) => String(s).toLowerCase())
  )
  const out = []
  const raw = Array.isArray(profiles) ? profiles : []

  for (const profile of raw) {
    const platesArr = Array.isArray(profile.plates) ? profile.plates : []
    for (const p of platesArr) {
      if (!p || !p._id) continue
      const st = String(p.status || "").toLowerCase()
      if (excluded.has(st)) continue
      out.push({ profile, plateEntry: p, plateId: p._id })
    }
  }

  return out
}
